import { Ref } from 'vue';
import { useCommercialApi } from '@/composables/UseCommercialApi';
import { handleOperationMessages } from '@/utils/handleOperationMessages';
import { 
	DriverInformationList, 
	VehicleInformationList, 
	DriverAddress, 
	DriverDetailRequest, 
	ViolationOrderApiResponse, 
	DriverDetailRequestBody,
	NewApiPolicyResponse,
	NewApiDriverInfo,
	NewApiInsuredParty
} from '@/models/DriverInformationModels';
import { assignDriverUnits, filterDriversByLastNameThreshold } from '@/utils/DriverUtils';
import { useOperationMessaging } from '@/composables/UseOperationMessaging';
import { CommercialApiConfig } from '@/services/CommercialApiConfig'
import { TelemetryContract } from '@/contracts/TelemetryContract';
import type { Tell } from '@/services/Tell';
import { OperationResult } from '@/contracts/OperationResult';
import { useAppStore } from '@/stores/app.store';


export async function getDrivers(
	policyNumber: Ref<string | null>,
	tell: Tell,
	telemetry: TelemetryContract,
): Promise<{ drivers: DriverInformationList; address: DriverAddress; insuredPartys: NewApiInsuredParty[] }> {

	const { withMessaging } = useOperationMessaging();
	const { getDrivers } = useCommercialApi(CommercialApiConfig);

	return await withMessaging('Fetching Drivers', async () => {
		const result = await getDrivers<NewApiPolicyResponse>(
			policyNumber.value ?? '',
			async (response: unknown) => {
				const typedResponse = response as OperationResult<NewApiPolicyResponse>;
				handleOperationMessages(typedResponse, tell, telemetry);
			}
		);

		if (!result.data) {
			throw new Error('No policy data received from API');
		}

		const data = result.data;
		
		telemetry.trackEvent({
			name: 'getDrivers_start',
			properties: {
				policyNumber: policyNumber.value || ''
			}
		});
		
		// Extract driver information from the API structure
		// Merge driver and licenseInfo to preserve MVR and violation prediction data
		const driverParties: NewApiDriverInfo[] = data.drivers?.map(d => ({
			...d.driver,
			violationPrediction: d.licenseInfo?.violationPrediction ?? d.driver.violationPrediction,
			motorVehicleReport: d.licenseInfo?.motorVehicleReport ?? d.driver.motorVehicleReport
		})) || [];

		// Extract insured parties (business entities)
		const insuredParties: NewApiInsuredParty[] = data.insuredPartys || [];

		// Get the primary insured party's address (usually the first one)
		const driverAddress: DriverAddress = insuredParties.length > 0 && insuredParties[0].address.length > 0
			? insuredParties[0].address[0]
			: {};

		// Create DriverInformationList from driver parties
		const driverInformationList = new DriverInformationList(driverParties);

		// Create VehicleInformationList from insured assets
		const vehicleInformationList = new VehicleInformationList(data.insuredAssets || []);

		// Assign driver units and filter by last name threshold
		const driversWithUnits = assignDriverUnits(driverInformationList, vehicleInformationList);
		const filteredDrivers = filterDriversByLastNameThreshold(driversWithUnits, 16);

		telemetry.trackEvent({
			name: 'getDrivers_success',
			properties: {
				driverCount: filteredDrivers.drivers.length.toString(),
				vehicleCount: vehicleInformationList.vehicles.length.toString(),
				insuredPartyCount: insuredParties.length.toString()
			}
		});

		return {
			drivers: filteredDrivers,
			address: driverAddress,
			insuredPartys: insuredParties
		};
	});
}

export async function getViolationPredictionRaw(
	policyNumber: Ref<string | null>,
	detailRequests: DriverDetailRequest[],
	persist: boolean
): Promise<ViolationOrderApiResponse> {
	const { getViolationPrediction: fetchViolationPrediction } = useCommercialApi(CommercialApiConfig);
	const appStore = useAppStore();

	const requestBody: DriverDetailRequestBody = 
	{
		persist,
		// Force MVR data being returned - configurable via Pinia store
		forceMvr: appStore.getForceMvr,
		syncLicenseRequests: detailRequests
	};

	return await fetchViolationPrediction(policyNumber.value ?? '', requestBody);
}


